// i18n area 'ioServerPairs' (ENEA HP-SAN-1 R19, 2026-10-24): the I/O server
// pair console — a Lustre OSS/MDS pair as ONE native rbd consumer of a Ceph
// cluster: one cephx per pair confined to the pools of its volumes, the
// credential handed once (sudo, sensitive read), the SAN-side fence
// (blocklist + lock release: two belts with Pacemaker's STONITH).

export const en = {
  ioServerPairs: {
    nav: 'I/O servers (Lustre)',
    title: 'I/O server pairs',
    hint: 'A pair (2–4 members) of Lustre I/O servers as ONE native rbd consumer of a Ceph cluster: one cephx identity per pair, confined to the pools of its own volumes — the pair’s cephx sees neither other volumes nor the cluster’s metadata. The volumes it exports are the OST/MDT/MGT targets Lustre formats on the servers.',
    name: 'Name',
    cluster: 'Ceph cluster',
    members: 'Members',
    phase: 'Phase',
    volumes: 'Volumes',
    caps: 'Caps',
    capsHint: 'The cephx caps as rendered: one «profile rbd pool=…» per pool of the pair’s volumes, never «*».',
    entity: 'cephx entity',
    metadataDeviceClass: 'Metadata device class',
    createdAt: 'Created at',
    noPermission: 'Your grants do not cover this action here',
    deleteHint:
      'A pair deletes only with no volume attached (detach them first); the entity is removed on the cluster by a task.',
    taskDone: 'Task succeeded',
    phases: {
      pending: 'pending (minting the key)',
      ready: 'ready',
      error: 'error',
      deleting: 'deleting'
    },

    createDialog: {
      title: 'Create I/O server pair',
      name: 'Name',
      nameHint: 'DNS-1123 label, unique in the scope among objects',
      cluster: 'Ceph cluster',
      clusterHint: 'rbd is Ceph’s: the pair lives on one Ceph cluster, immutable afterwards',
      noClusters: 'No Ceph cluster in reach — create one first',
      members: 'Members (2–4)',
      memberName: 'Server name',
      memberAddresses: 'Fabric addresses',
      memberAddressesHint:
        'IPv4 addresses of this server on the fabric, separated by spaces or commas (1–8). The fence blocklists ALL of them.',
      addMember: 'Add member',
      removeMember: 'Remove member',
      fsname: 'Lustre fsname (optional)',
      fsnameHint: 'As in «mkfs.lustre --fsname», at most 8 characters — descriptive only',
      metadataDeviceClass: 'Metadata device class',
      metadataDeviceClassHint:
        'The CRUSH device class a Lustre MDT/MGT exported to this pair must ride (replicated ×3 on it). Default nvme; on HP nodes every disk is NVMe, so dedicate the metadata disks with a class of your own (e.g. «nvme-meta», «ceph osd crush set-device-class») and name it here — a class with no OSD is refused by the server.',
      refusals: {
        count: 'A pair has 2 to 4 members',
        name: 'Each member needs a DNS-1123 name',
        address: 'Addresses are bare IPv4 literals',
        addressCount: 'Each member has 1 to 8 addresses',
        duplicateName: 'Member names are distinct',
        duplicateAddress: 'An address belongs to one member only'
      },
      submit: 'Create'
    },

    tabs: {
      overview: 'Pair',
      members: 'Members',
      volumes: 'Volumes',
      credential: 'Credential'
    },

    fence: {
      none: 'in service',
      fenced: 'fenced',
      fencedAt: 'fenced {at} by {by}',
      blocklisted: 'Blocklisted entries',
      fence: 'Fence',
      unfence: 'Unfence',
      fenceTitle: 'Fence {member} of {pair}',
      unfenceTitle: 'Unfence {member} of {pair}',
      twoBelts:
        'The SAN-side fence does not replace Pacemaker’s STONITH: two belts. Every OSD refuses the fenced server’s operations (blocklist of all its addresses) and its rbd locks are released — the server may still be powered on and believe it writes: it gets EBLOCKLISTED.',
      waitTask:
        'The caller waits for the task’s outcome, not the 202: the fence is complete only when the task is «succeeded» — a failed task means the fence did not happen (no half-fenced member).',
      unfenceHint:
        'Removes exactly the blocklist entries the fence put; the server can open the images again with a new session.',
      memberLabel: 'Member',
      confirmLabel: 'Type the pair’s name to confirm',
      confirmHint: 'Type «{name}»',
      fenceConfirm: 'Fence the member',
      unfenceConfirm: 'Unfence the member',
      started: 'Task {task} started — waiting for its outcome…',
      startedNoTask: 'Accepted without a task reference',
      polling: 'Waiting for the task…',
      succeeded: 'Task succeeded: the member is {state}',
      failed: 'Task failed: the fence did NOT happen — {detail}',
      taskLink: 'Open the task'
    },

    volumesTab: {
      none: 'No volume exported to this pair yet',
      name: 'Volume',
      role: 'Lustre role',
      holder: 'Holder (lock owner)',
      holderNone: 'nobody',
      watchers: 'Watchers',
      attach: 'Attach volume',
      attachTitle: 'Export a volume to {pair}',
      attachSelect: 'Volume',
      attachNone: 'No attachable volume: ready, on the same Ceph cluster, not yet exported to a pair',
      attachHint:
        'The volume keeps its own pool: attaching adds the pool (and its EC data pool) to the pair’s caps — the key does not change. A volume with a target is refused (409).',
      attachConfirm: 'Attach',
      detach: 'Detach',
      detachHeld: 'Still held by a member: unmount it or fence the member first (the server refuses with 409)',
      detachHint: 'Takes the pool out of the pair’s caps.'
    },

    credential: {
      show: 'Show the credential (sudo)',
      once: 'The key is shown once per read: this read is audited as a sensitive read. Install ceph.conf and the keyring on BOTH servers of the pair (R20 runbook).',
      entity: 'Entity',
      fsid: 'fsid',
      monitors: 'Monitors',
      key: 'Key',
      downloadConf: 'Download ceph.conf',
      downloadKeyring: 'Download keyring',
      saved: 'Saved {name}',
      rotate: 'Rotate the key',
      rotateTitle: 'Rotate the key of {pair}',
      rotateSentence:
        'The rotation revokes the old key on the cluster (ceph auth rm + a fresh key with the same caps): the members must be updated with the new keyring — one member at a time, R20 runbook. The volumes the old key holds lose their session.',
      rotateConfirm: 'Rotate',
      rotatedAt: 'Last rotation',
      neverRotated: 'never',
      noPermission: 'ioserverpair.credential and sudo are required to read the key',
      notReady: 'The key exists once the pair is ready (the apply task mints it).'
    }
  }
}

export const it: typeof en = {
  ioServerPairs: {
    nav: 'Server di I/O (Lustre)',
    title: 'Coppie di server di I/O',
    hint: 'Una coppia (2–4 membri) di server di I/O di Lustre come UN consumatore nativo rbd di un cluster Ceph: una identità cephx per coppia, confinata ai pool dei suoi volumi — il cephx della coppia non vede altri volumi né i metadati del cluster. I volumi che esporta sono gli OST/MDT/MGT che Lustre formatta sui server.',
    name: 'Nome',
    cluster: 'Cluster Ceph',
    members: 'Membri',
    phase: 'Fase',
    volumes: 'Volumi',
    caps: 'Caps',
    capsHint: 'Le caps cephx come sono rese: un «profile rbd pool=…» per ogni pool dei volumi della coppia, mai «*».',
    entity: 'Entità cephx',
    metadataDeviceClass: 'Classe dei dischi dei metadati',
    createdAt: 'Creata il',
    noPermission: 'I tuoi grant non coprono questa azione qui',
    deleteHint:
      'Una coppia si cancella solo senza volumi attaccati (staccali prima); l’entità viene tolta dal cluster da un task.',
    taskDone: 'Task riuscito',
    phases: {
      pending: 'in attesa (conio della chiave)',
      ready: 'pronta',
      error: 'errore',
      deleting: 'in cancellazione'
    },

    createDialog: {
      title: 'Crea coppia di server di I/O',
      name: 'Nome',
      nameHint: 'Etichetta DNS-1123, unica nello scope fra gli oggetti',
      cluster: 'Cluster Ceph',
      clusterHint: 'rbd è di Ceph: la coppia vive su un cluster Ceph, immutabile dopo',
      noClusters: 'Nessun cluster Ceph raggiungibile: creane uno prima',
      members: 'Membri (2–4)',
      memberName: 'Nome del server',
      memberAddresses: 'Indirizzi del fabric',
      memberAddressesHint:
        'Indirizzi IPv4 di questo server sul fabric, separati da spazi o virgole (1–8). La recinzione li blocklista TUTTI.',
      addMember: 'Aggiungi membro',
      removeMember: 'Togli membro',
      fsname: 'fsname Lustre (facoltativo)',
      fsnameHint: 'Come in «mkfs.lustre --fsname», al massimo 8 caratteri — solo descrittivo',
      metadataDeviceClass: 'Classe dei dischi dei metadati',
      metadataDeviceClassHint:
        'La classe CRUSH su cui deve stare un MDT/MGT di Lustre esportato a questa coppia (replica ×3 su di essa). Default nvme; sui nodi HP tutti i dischi sono NVMe, quindi dedica i dischi dei metadati con una classe tua (es. «nvme-meta», «ceph osd crush set-device-class») e nominala qui — una classe senza OSD è rifiutata dal server.',
      refusals: {
        count: 'Una coppia ha da 2 a 4 membri',
        name: 'Ogni membro vuole un nome DNS-1123',
        address: 'Gli indirizzi sono letterali IPv4 nudi',
        addressCount: 'Ogni membro ha da 1 a 8 indirizzi',
        duplicateName: 'I nomi dei membri sono distinti',
        duplicateAddress: 'Un indirizzo appartiene a un solo membro'
      },
      submit: 'Crea'
    },

    tabs: {
      overview: 'Coppia',
      members: 'Membri',
      volumes: 'Volumi',
      credential: 'Credenziale'
    },

    fence: {
      none: 'in servizio',
      fenced: 'recintato',
      fencedAt: 'recintato {at} da {by}',
      blocklisted: 'Voci in blocklist',
      fence: 'Recinta',
      unfence: 'Libera',
      fenceTitle: 'Recinta {member} di {pair}',
      unfenceTitle: 'Libera {member} di {pair}',
      twoBelts:
        'La recinzione dal lato SAN non sostituisce lo STONITH di Pacemaker: due cinture. Ogni OSD rifiuta le operazioni del server recintato (blocklist di tutti i suoi indirizzi) e i suoi lock rbd vengono rilasciati — il server può essere ancora acceso e credere di scrivere: riceve EBLOCKLISTED.',
      waitTask:
        'Il chiamante attende l’esito del task, non il 202: la recinzione è completa solo quando il task è «succeeded» — un task fallito vuol dire che la recinzione non è avvenuta (nessun membro «quasi recintato»).',
      unfenceHint:
        'Toglie esattamente le voci di blocklist che la recinzione ha messo; il server può riaprire le immagini con una sessione nuova.',
      memberLabel: 'Membro',
      confirmLabel: 'Scrivi il nome della coppia per confermare',
      confirmHint: 'Scrivi «{name}»',
      fenceConfirm: 'Recinta il membro',
      unfenceConfirm: 'Libera il membro',
      started: 'Task {task} avviato — in attesa dell’esito…',
      startedNoTask: 'Accettato senza riferimento al task',
      polling: 'In attesa del task…',
      succeeded: 'Task riuscito: il membro è {state}',
      failed: 'Task fallito: la recinzione NON è avvenuta — {detail}',
      taskLink: 'Apri il task'
    },

    volumesTab: {
      none: 'Nessun volume ancora esportato a questa coppia',
      name: 'Volume',
      role: 'Ruolo Lustre',
      holder: 'Possessore (lock)',
      holderNone: 'nessuno',
      watchers: 'Watcher',
      attach: 'Attacca volume',
      attachTitle: 'Esporta un volume a {pair}',
      attachSelect: 'Volume',
      attachNone: 'Nessun volume attaccabile: pronto, sullo stesso cluster Ceph, non ancora esportato a una coppia',
      attachHint:
        'Il volume resta nel suo pool: attaccarlo aggiunge il pool (e il suo data pool EC) alle caps della coppia — la chiave non cambia. Un volume con un target è rifiutato (409).',
      attachConfirm: 'Attacca',
      detach: 'Stacca',
      detachHeld: 'Ancora tenuto da un membro: smontalo o recinta il membro prima (il server rifiuta con 409)',
      detachHint: 'Toglie il pool dalle caps della coppia.'
    },

    credential: {
      show: 'Mostra la credenziale (sudo)',
      once: 'La chiave si mostra una volta per lettura: questa lettura è registrata nell’audit come lettura sensibile. Installa ceph.conf e il keyring su ENTRAMBI i server della coppia (runbook R20).',
      entity: 'Entità',
      fsid: 'fsid',
      monitors: 'Monitor',
      key: 'Chiave',
      downloadConf: 'Scarica ceph.conf',
      downloadKeyring: 'Scarica il keyring',
      saved: 'Salvato {name}',
      rotate: 'Ruota la chiave',
      rotateTitle: 'Ruota la chiave di {pair}',
      rotateSentence:
        'La rotazione revoca la vecchia chiave sul cluster (ceph auth rm + una chiave nuova con le stesse caps): i membri vanno aggiornati col nuovo keyring — un membro alla volta, runbook R20. I volumi che la vecchia chiave tiene perdono la sessione.',
      rotateConfirm: 'Ruota',
      rotatedAt: 'Ultima rotazione',
      neverRotated: 'mai',
      noPermission: 'Per leggere la chiave servono ioserverpair.credential e sudo',
      notReady: 'La chiave esiste quando la coppia è pronta (la conia il task di apply).'
    }
  }
}
